import { useCookies } from 'react-cookie';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from '@tanstack/react-router';

import { useAuthContext } from '../auth';
import { CookieConsentContextType } from './types';
import { CookieConsentContext } from './consent-context';

interface CookieConsentProviderProps {
  children: React.ReactNode;
  disabeledPaths: string[];
}

export const CookieConsentProvider = ({ children, disabeledPaths }: CookieConsentProviderProps) => {
  const { authenticated, loading, user } = useAuthContext();
  const [cookies, setCookie, removeCookie] = useCookies();

  const pathname = useLocation({
    select: (location) => location.pathname,
  });
  const isPathDisabled = disabeledPaths.includes(pathname);

  const getCookieKey = useCallback((userId: string) => `cookieConsentPreference_${userId}`, []);

  const giveConsent = useCallback(() => {
    if (user?.id) {
      setCookie(getCookieKey(user.id), JSON.stringify({ consentGiven: true, userId: user?.id }), {
        path: '/',
        maxAge: 31536000,
      });
      // console.log('Consent given for user:', user?.id);
    } else {
      console.warn('Cannot give consent: userId is not available');
    }
  }, [getCookieKey, setCookie, user?.id]);

  const revokeConsent = useCallback(() => {
    if (user?.id) {
      removeCookie(getCookieKey(user.id), { path: '/' });
      // console.log('Consent revoked for user:', user.id);
    } else {
      console.warn('Cannot revoke consent: userId is not available');
    }
  }, [removeCookie, user?.id, getCookieKey]);

  const memoizedValue = useMemo<CookieConsentContextType>(() => {
    let consentGiven = false;
    let consentUserId = null;
    if (user?.id) {
      const userCookie = cookies[getCookieKey(user?.id)];
      try {
        if (userCookie) {
          const { consentGiven: parsedConsentGiven, userId: parsedUserId } = userCookie;
          consentGiven = parsedConsentGiven;
          consentUserId = parsedUserId;
        }
      } catch (error) {
        console.error('Error parsing cookieConsent:', error);
      }
    }

    const currentUserHasConsented = consentUserId === user?.id;

    return {
      consentGiven,
      giveConsent,
      revokeConsent,
      shouldDisplayCookieBanner:
        (!consentGiven || !currentUserHasConsented) && !loading && authenticated && !isPathDisabled,
    };
  }, [
    authenticated,
    cookies,
    getCookieKey,
    giveConsent,
    isPathDisabled,
    loading,
    revokeConsent,
    user?.id,
  ]);

  return (
    <CookieConsentContext.Provider value={memoizedValue}>{children}</CookieConsentContext.Provider>
  );
};
